import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from "recharts";
import { TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AccountValuePoint } from "@/lib/hyperliquid";

interface EquityCurveProps {
  data?: AccountValuePoint[];
  loading?: boolean;
  className?: string;
  rangeDays?: number;
  currentValue?: number | null;
}

export const EquityCurve = ({ 
  data = [], 
  loading = false,
  className,
  rangeDays = 30,
  currentValue = null
}: EquityCurveProps) => {
  if (loading) {
    return (
      <div className={cn("stat-card h-[400px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div className="h-5 w-32 bg-muted/50 rounded animate-pulse" />
          <div className="h-4 w-24 bg-muted/50 rounded animate-pulse" />
        </div>
        <div className="h-72 w-full bg-muted/30 rounded animate-pulse" />
      </div>
    );
  }

  const chartData = data.map(point => ({
    ts_ms: point.ts_ms,
    value: point.value,
    date: new Date(point.ts_ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }));

  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || 0;
  const displayCurrentValue = currentValue != null ? currentValue : lastValue;
  const percentChange = firstValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;
  const isPositive = percentChange >= 0;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0].payload;
      return (
        <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg p-3 shadow-xl">
          <p className="text-xs text-muted-foreground mb-1.5">
            {new Date(point.ts_ms).toLocaleString()}
          </p>
          <p className="text-xl font-bold font-mono text-primary">
            ${payload[0].value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className={cn("stat-card h-[400px]", className)}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-base font-semibold mb-1">Account Value</h3>
            <span className="text-xs text-muted-foreground">Last {rangeDays} days</span>
          </div>
        </div>
        <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
          No portfolio data available. Click "Sync" to fetch.
        </div>
      </div>
    );
  }

  return (
    <div className={cn("stat-card h-[400px] group", className)}>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold">Account Value</h3>
              {chartData.length > 0 && (
                <div className={cn(
                  "flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full",
                  isPositive ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"
                )}>
                  <TrendingUp className={cn("w-3 h-3", !isPositive && "rotate-180")} />
                  {isPositive ? "+" : ""}{percentChange.toFixed(2)}%
                </div>
              )}
            </div>
            <span className="text-xs text-muted-foreground">Last {rangeDays} days</span>
          </div>
          {chartData.length > 0 && (
            <div className="text-right">
              <div className="text-2xl font-bold font-mono">
                ${displayCurrentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-muted-foreground">Current Balance</div>
            </div>
          )}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="hsl(var(--border))" 
                strokeOpacity={0.3}
                vertical={false}
              />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                dy={10}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                domain={['auto', 'auto']}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: "hsl(var(--primary))", strokeWidth: 1, strokeDasharray: "5 5" }} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#colorValue)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
